/**
 * Projeção de ponto em polyline (coords GeoJSON [lng, lat]) e distância ao longo da rota.
 * Espelha a lógica de api/utils/enderecoValidacao.js (closestPointOnPolylineFast).
 */

import { haversineMeters } from '@/utils/haversine';

/**
 * @param {number} lat
 * @param {number} lng
 * @param {Array<[number, number]>} coordinates - [lng, lat][]
 * @returns {{ lat: number, lng: number, distance: number, segmentIndex: number, t: number } | null}
 */
export function closestPointOnPolyline(lat, lng, coordinates) {
    if (!coordinates || coordinates.length < 2) return null;
    const cos0 = Math.cos((lat * Math.PI) / 180);
    const kx = 111320 * cos0;
    const ky = 110540;
    const px = lng;
    const py = lat;
    let bestSq = Infinity;
    let bestLat = lat;
    let bestLng = lng;
    let bestSeg = 0;
    let bestT = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
        const ax = Number(coordinates[i][0]);
        const ay = Number(coordinates[i][1]);
        const bx = Number(coordinates[i + 1][0]);
        const by = Number(coordinates[i + 1][1]);
        const dx = bx - ax;
        const dy = by - ay;
        const len2 = dx * dx + dy * dy;
        const t = len2 < 1e-18 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
        const cx = ax + t * dx;
        const cy = ay + t * dy;
        const mx = (cx - px) * kx;
        const my = (cy - py) * ky;
        const d2 = mx * mx + my * my;
        if (d2 < bestSq) {
            bestSq = d2;
            bestLat = cy;
            bestLng = cx;
            bestSeg = i;
            bestT = t;
        }
    }
    if (bestSq === Infinity) return null;
    return {
        lat: bestLat,
        lng: bestLng,
        distance: haversineMeters(lat, lng, bestLat, bestLng),
        segmentIndex: bestSeg,
        t: bestT,
    };
}

/**
 * Distância ao longo da polyline até o ponto projetado (metros).
 * @param {Array<[number, number]>} coordinates
 * @param {{ segmentIndex: number, t: number, lat: number, lng: number }} hit
 */
export function distanceAlongPolyline(coordinates, hit) {
    if (!coordinates || !hit || coordinates.length < 2) return 0;
    let along = 0;
    const seg = Math.max(0, Math.min(coordinates.length - 2, Number(hit.segmentIndex) || 0));
    for (let i = 0; i < seg; i++) {
        along += haversineMeters(
            coordinates[i][1],
            coordinates[i][0],
            coordinates[i + 1][1],
            coordinates[i + 1][0],
        );
    }
    const ax = coordinates[seg][0];
    const ay = coordinates[seg][1];
    const bx = coordinates[seg + 1][0];
    const by = coordinates[seg + 1][1];
    const t = Number(hit.t);
    const cx = ax + (Number.isFinite(t) ? t : 0) * (bx - ax);
    const cy = ay + (Number.isFinite(t) ? t : 0) * (by - ay);
    along += haversineMeters(ay, ax, cy, cx);
    return along;
}

/**
 * Parse defensivo de geojson de rota (objeto, string JSON ou string duplamente codificada).
 * @returns {object | null}
 */
export function parseRouteGeojson(raw) {
    let value = raw;
    let guard = 0;
    while (typeof value === 'string' && guard < 3) {
        guard += 1;
        try {
            value = JSON.parse(value);
        } catch (e) {
            return null;
        }
    }
    return value && typeof value === 'object' ? value : null;
}

/**
 * Extrai a primeira LineString [lng,lat][] de um GeoJSON (Feature/FeatureCollection/Geometry).
 * Aceita também string JSON (comum após persistência/histórico).
 * @returns {Array<[number, number]> | null}
 */
export function extractLineStringCoordinates(geojson) {
    const parsed = parseRouteGeojson(geojson);
    if (!parsed) return null;
    if (parsed.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
        for (let i = 0; i < parsed.features.length; i++) {
            const coords = extractLineStringCoordinates(parsed.features[i]);
            if (coords && coords.length >= 2) return coords;
        }
        return null;
    }
    const geom = parsed.type === 'Feature' ? parsed.geometry : parsed;
    if (!geom || !geom.coordinates) return null;
    if (geom.type === 'LineString' && Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
        return geom.coordinates;
    }
    if (geom.type === 'MultiLineString' && Array.isArray(geom.coordinates)) {
        let best = null;
        for (let i = 0; i < geom.coordinates.length; i++) {
            const c = geom.coordinates[i];
            if (Array.isArray(c) && c.length >= 2 && (!best || c.length > best.length)) {
                best = c;
            }
        }
        return best;
    }
    return null;
}

/**
 * Converte o campo `shape` da rota (`[{ lt, lg }, ...]` ou JSON string) em coords GeoJSON [lng,lat][].
 * @returns {Array<[number, number]> | null}
 */
export function coordinatesFromRouteShape(shape) {
    let parsed = shape;
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch (e) {
            return null;
        }
    }
    if (!Array.isArray(parsed) || parsed.length < 2) return null;
    const coords = [];
    for (let i = 0; i < parsed.length; i++) {
        const p = parsed[i];
        if (!p) continue;
        const lng = Number(p.lg != null ? p.lg : p.lng);
        const lat = Number(p.lt != null ? p.lt : p.lat);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
        coords.push([lng, lat]);
    }
    return coords.length >= 2 ? coords : null;
}

/**
 * Resolve a polyline [lng,lat][] de uma rota de plotagem/histórico.
 * Ordem: geojson → shape → Leaflet polyline (`polyline` arg ou `route.rota`).
 * @param {object|null|undefined} route
 * @param {object|null|undefined} [polyline] Leaflet polyline opcional (fora de data reativa)
 * @returns {Array<[number, number]> | null}
 */
export function resolveRoutePolylineCoordinates(route, polyline = null) {
    if (!route) return null;
    const fromGeo = extractLineStringCoordinates(route.geojson);
    if (fromGeo && fromGeo.length >= 2) return fromGeo;
    const fromShape = coordinatesFromRouteShape(route.shape);
    if (fromShape && fromShape.length >= 2) return fromShape;
    const layer = polyline || route.rota;
    if (layer && typeof layer.getLatLngs === 'function') {
        const latlngs = layer.getLatLngs();
        if (Array.isArray(latlngs) && latlngs.length >= 2) {
            const coords = [];
            for (let i = 0; i < latlngs.length; i++) {
                const p = latlngs[i];
                if (!p) continue;
                const lat = Number(p.lat);
                const lng = Number(p.lng);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
                coords.push([lng, lat]);
            }
            if (coords.length >= 2) return coords;
        }
    }
    return null;
}

/**
 * Garante Feature GeoJSON LineString a partir de coords [lng,lat][].
 * @returns {object | null}
 */
export function lineStringFeatureFromCoordinates(coordinates) {
    if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
    return {
        type: 'Feature',
        properties: {},
        geometry: {
            type: 'LineString',
            coordinates,
        },
    };
}

export { haversineMeters };
